import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { AuthCard } from "@/components/auth/AuthCard";
import { FormField } from "@/components/auth/FormField";
import { OtpInput } from "@/components/auth/OtpInput";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { useRedeemParentInvite } from "@/hooks/useParent";
import {
  clearPendingParentInvite,
  getPendingParentInvite,
  setPendingParentInvite,
} from "@/lib/parent-invite-storage";

const schema = z.object({
  email: z.string().email("Enter a valid email"),
});

type FormValues = z.infer<typeof schema>;

/**
 * One flow for both sign-in and sign-up: enter an email, get a 6-digit code.
 * If the email doesn't have an account yet, the backend (CreateAuthChallenge
 * trigger) silently provisions one and this proceeds exactly like an
 * existing user signing in -- the client never needs to know which case it is.
 */
export function ContinueWithEmailPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextRaw = params.get("next");
  const next = nextRaw && nextRaw.startsWith("/") && !nextRaw.startsWith("//") ? nextRaw : "/dashboard";
  // Presence of ?invite= switches this into the parent-invite variant: a
  // student shared this link, so the email entered here signs up (or signs
  // in) as a PARENT and auto-redeems the code once verified. No separate
  // account-type toggle exists -- this is the only variant of the page.
  const inviteCode = params.get("invite");
  const { requestOtp, verifyOtp } = useAuth();
  const redeemInvite = useRedeemParentInvite();
  const [stage, setStage] = useState<"email" | "code">("email");
  const [submitting, setSubmitting] = useState(false);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);

  useEffect(() => {
    // Persisted defensively so the code survives a detour through
    // /auth/verify (legacy unconfirmed password account edge case) --
    // the common path never needs to read it back.
    if (inviteCode) setPendingParentInvite(inviteCode);
  }, [inviteCode]);
  const {
    register,
    handleSubmit,
    getValues,
    setError,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onRequest = handleSubmit(async (values) => {
    const email = values.email.trim().toLowerCase();
    setSubmitting(true);
    try {
      const result = await requestOtp(email, inviteCode ? "PARENT" : undefined);
      const step = result.nextStep?.signInStep;

      if (result.isSignedIn) {
        navigate(next, { replace: true });
        return;
      }

      if (step === "CONFIRM_SIGN_UP") {
        // Pre-dates email-OTP sign-up: a password account that was created
        // but never confirmed. Send them to finish that the old way.
        toast.message("Verify your email first, then try again.");
        navigate(`/auth/verify?email=${encodeURIComponent(email)}${nextRaw ? `&next=${encodeURIComponent(nextRaw)}` : ""}`);
        return;
      }

      if (step === "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
        setStage("code");
        toast.success("Check your inbox for a 6-digit code.");
        return;
      }

      setError("email", { message: "We could not send a code. Try password sign-in instead." });
    } catch (err) {
      const message = (err as { message?: string }).message || "Could not send code.";
      if (message.toLowerCase().includes("not confirmed")) {
        toast.message("Verify your email first, then try again.");
        navigate(`/auth/verify?email=${encodeURIComponent(email)}${nextRaw ? `&next=${encodeURIComponent(nextRaw)}` : ""}`);
        return;
      }
      setError("email", { message });
    } finally {
      setSubmitting(false);
    }
  });

  const onVerify = async () => {
    if (code.length !== 6) {
      setCodeError("Enter the 6-digit code");
      return;
    }
    setSubmitting(true);
    try {
      const result = await verifyOtp(code);
      if (result.isSignedIn) {
        const pendingInvite = inviteCode || getPendingParentInvite();
        if (pendingInvite) {
          try {
            await redeemInvite.mutateAsync({ invite_code: pendingInvite });
          } catch {
            // Swallow -- an expired/invalid code lands the parent on
            // ParentHomePage's empty state, which explains what to do next.
          }
          clearPendingParentInvite();
          navigate("/parent", { replace: true });
          return;
        }
        navigate(next, { replace: true });
        return;
      }

      const step = result.nextStep?.signInStep;
      if (step === "CONFIRM_SIGN_UP") {
        const email = getValues("email").trim().toLowerCase();
        toast.message("Verify your email first, then sign in.");
        navigate(`/auth/verify?email=${encodeURIComponent(email)}${nextRaw ? `&next=${encodeURIComponent(nextRaw)}` : ""}`);
        return;
      }

      setCodeError("That code was accepted, but sign-in did not complete. Request a fresh code and try again.");
    } catch (err) {
      const message = (err as { message?: string }).message || "Invalid code.";
      setCodeError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthCard
      title={inviteCode ? "You've been invited to StaxED" : "Continue with email"}
      description={
        stage === "email"
          ? inviteCode
            ? "Your child invited you to see how their studies are going. Enter your email and we'll send you a 6-digit code to get started."
            : "Enter your email and we'll send you a 6-digit code -- for existing accounts and new ones alike."
          : `Enter the code we sent to ${getValues("email")}.`
      }
      footer={
        stage === "email" ? (
          <p>
            Prefer a password?{" "}
            <Link
              to={nextRaw ? `/auth/login?next=${encodeURIComponent(nextRaw)}` : "/auth/login"}
              className="font-medium text-brand hover:underline"
            >
              Use password instead
            </Link>
          </p>
        ) : null
      }
    >
      {stage === "email" ? (
        <form onSubmit={onRequest} className="flex flex-col gap-4" noValidate>
          <FormField id="email" label="Email" error={errors.email?.message}>
            <Input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoFocus
              placeholder="you@example.com"
              aria-invalid={!!errors.email}
              {...register("email")}
            />
          </FormField>
          <Button type="submit" fullWidth loading={submitting}>
            Email me a code
          </Button>
        </form>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label>6-digit code</Label>
            <OtpInput
              value={code}
              onChange={(v) => {
                setCode(v);
                setCodeError(null);
              }}
              autoFocus
              inputMode="numeric"
            />
            {codeError ? <p className="text-sm text-danger">{codeError}</p> : null}
          </div>
          <Button onClick={onVerify} fullWidth loading={submitting}>
            Continue
          </Button>
          <button
            type="button"
            onClick={() => setStage("email")}
            className="text-sm text-text-muted hover:text-text self-center"
          >
            Use a different email
          </button>
        </div>
      )}
    </AuthCard>
  );
}
